/**
 * <collection-filters> — storefront filtering + sorting for the collection page without a full reload.
 *
 * Markup contract (sections/main-collection.liquid):
 *   form[data-filters-form]  facet inputs ([data-facet]), price inputs ([data-price-input]), select[data-sort-select]
 *   [data-sort-radio]        sheet radios, kept in sync with the select
 *   [data-filter-sheet]      <dialog> (bottom sheet on mobile, side panel on desktop); [data-filter-open] / [data-filter-close]
 *   [data-results]           region replaced with the Section Rendering API response (grid + pagination)
 *   [data-results-count] [data-active-count] [data-filter-apply]   updated after each request
 * Without JS the form simply submits (GET) and the page reloads.
 */
class CollectionFilters extends HTMLElement {
  #abort = new AbortController();
  #request = null;

  connectedCallback() {
    const { signal } = this.#abort;
    this.sectionId = this.dataset.sectionId;
    this.form = this.querySelector('[data-filters-form]');
    this.sheet = this.querySelector('[data-filter-sheet]');
    this.select = this.querySelector('[data-sort-select]');
    if (!this.form) return;

    const submit = RM.debounce(() => this.#apply(), 350);

    this.form.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
      if (target.matches('[data-sort-radio]') && this.select) {
        this.select.value = target.value;
      }
      if (target === this.select) {
        for (const radio of this.querySelectorAll('[data-sort-radio]')) radio.checked = radio.value === target.value;
      }
      if (target.matches('[data-facet], [data-price-input], [data-sort-select], [data-sort-radio]')) submit();
    }, { signal });

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#apply();
      this.#closeSheet();
    }, { signal });

    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      if (target.closest('[data-filter-open]')) return this.#openSheet(target.closest('[data-filter-open]'));
      if (target.closest('[data-filter-close]')) return this.#closeSheet();
      if (target === this.sheet) return this.#closeSheet();
      const clear = target.closest('[data-filter-clear], [data-chip-all]');
      if (clear instanceof HTMLAnchorElement && !event.metaKey && !event.ctrlKey) {
        event.preventDefault();
        this.#fetchUrl(clear.href);
      }
    }, { signal });

    this.sheet?.addEventListener('close', () => {
      document.body.classList.remove('has-modal-open');
      this.opener?.focus();
    }, { signal });

    window.addEventListener('popstate', () => window.location.reload(), { signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  #openSheet(opener) {
    if (!this.sheet || this.sheet.open) return;
    this.opener = opener;
    this.sheet.showModal();
    document.body.classList.add('has-modal-open');
  }

  #closeSheet() {
    if (this.sheet?.open) this.sheet.close();
  }

  /** Build the filter URL from the form: drop empty values, keep sort only when it differs from the default. */
  #apply() {
    const url = new URL(this.form.action, window.location.origin);
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(this.form)) {
      if (value === '' || key.startsWith('sort-radio')) continue;
      params.append(key, String(value));
    }
    url.search = params.toString();
    this.#fetchUrl(url.toString());
  }

  async #fetchUrl(href) {
    this.#request?.abort();
    this.#request = new AbortController();
    const results = this.querySelector('[data-results]');
    results?.setAttribute('aria-busy', 'true');
    try {
      const target = new URL(href, window.location.origin);
      const html = await RM.fetchSection(this.sectionId, {
        url: target.pathname,
        params: Object.fromEntries(target.searchParams),
        signal: this.#request.signal,
      });
      const doc = new DOMParser().parseFromString(html, 'text/html');
      this.#render(doc);
      window.history.pushState({}, '', `${target.pathname}${target.search}`);
    } catch (error) {
      if (error.name !== 'AbortError') window.location.href = href;
    } finally {
      results?.removeAttribute('aria-busy');
    }
  }

  #render(doc) {
    const nextResults = doc.querySelector('[data-results]');
    const results = this.querySelector('[data-results]');
    if (nextResults && results) results.innerHTML = nextResults.innerHTML;

    const nextCount = doc.querySelector('[data-results-count]');
    const count = this.querySelector('[data-results-count]');
    if (nextCount && count) count.textContent = nextCount.textContent.trim();

    const nextApply = doc.querySelector('[data-filter-apply]');
    const apply = this.querySelector('[data-filter-apply]');
    if (nextApply && apply) apply.textContent = nextApply.textContent.trim();

    // Facet availability: counts, disabled and active state.
    for (const next of doc.querySelectorAll('[data-facet]')) {
      const current = this.querySelector(`[data-facet="${CSS.escape(next.dataset.facet)}"]`);
      if (!current) continue;
      current.disabled = next.disabled;
      current.checked = next.checked;
      const label = current.closest('label');
      label?.classList.toggle('is-disabled', next.disabled);
      label?.classList.toggle('is-active', next.checked);
      const nextCountEl = next.closest('label')?.querySelector('[data-facet-count]');
      const countEl = label?.querySelector('[data-facet-count]');
      if (nextCountEl && countEl) countEl.textContent = nextCountEl.textContent;
    }

    // "All" chip state + active count badge.
    const nextAll = doc.querySelector('[data-chip-all]');
    const all = this.querySelector('[data-chip-all]');
    if (nextAll && all) {
      all.classList.toggle('is-active', nextAll.classList.contains('is-active'));
      all.setAttribute('aria-current', nextAll.getAttribute('aria-current') || 'false');
      all.href = nextAll.href;
      const nextAllCount = nextAll.querySelector('.collection-chip__count');
      const allCount = all.querySelector('.collection-chip__count');
      if (nextAllCount && allCount) allCount.textContent = nextAllCount.textContent;
    }
    const nextBadge = doc.querySelector('[data-active-count]');
    const badge = this.querySelector('[data-active-count]');
    if (nextBadge && badge) {
      badge.textContent = nextBadge.textContent;
      badge.hidden = nextBadge.hidden;
    }
    const nextClear = doc.querySelector('[data-filter-clear]');
    const clear = this.querySelector('[data-filter-clear]');
    if (nextClear && clear) clear.href = nextClear.href;

    const live = results?.querySelector('[data-results-grid]');
    if (live) RM.announce(this.querySelector('[data-results-count]')?.textContent || '');
  }
}

if (!customElements.get('collection-filters')) customElements.define('collection-filters', CollectionFilters);
